import PageHeader from '../../components/PageHeader';
import FilterPanel from '../../components/FilterPanel';
import { useState } from 'react';
import DataTable from '../../components/DataTable';
import { DAY_NAMES } from '../../constants/openingHours';
import { formatDate, langValue } from '../../utils/formatters';
import { emptyPlaceForm, toPlaceForm, toPlacePayload } from '../../utils/placeForm';
import PlaceDialog from './PlaceDialog';
import { Button, Typography, MenuItem, Stack, TextField } from '@mui/material';
const PlacesPage = ({
  places,
  filters,
  setFilters,
  loadPlaces,
  onSavePlace,
  onLoadNearbyPlaceFootprints,
  onLoadContainerManagement,
  onUpdateContainerFloors,
  onManageContainerFloorUnit,
  onAssignPlaceToContainer,
  onRemovePlaceFromContainer,
  onDeletePlace,
  behaviorTypes,
  categories,
  loading,
  pageTitle = 'רשימת מקומות',
  itemLabel = 'מקומות',
  showPlaceKindFilter = true,
}) => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [highlightedPlaceId, setHighlightedPlaceId] = useState(null);
  const [placeForm, setPlaceForm] = useState(emptyPlaceForm);
  const [duplicateTargets, setDuplicateTargets] = useState(() =>
    DAY_NAMES.map((_, day) => (day + 1) % 7),
  );
  const openPlace = (place) => {
    setHighlightedPlaceId(place.placeId);
    setSelectedPlace(place);
    setPlaceForm(toPlaceForm(place));
  };

  const closePlace = () => {
    setSelectedPlace(null);
  };

  const updateNested = (section, lang, value) => {
    setPlaceForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [lang]: value,
      },
    }));
  };

  const updateDayRanges = (dayIndex, updater) => {
    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) =>
        day.day === dayIndex
          ? {
            ...day,
            ranges: updater(day.ranges),
          }
          : day,
      ),
    }));
  };

  const addRange = (dayIndex) => {
    updateDayRanges(dayIndex, (ranges) => [
      ...ranges,
      {
        open: '08:00',
        close: '17:00',
        isOpen24Hours: false,
      },
    ]);
  };

  const removeRange = (dayIndex, rangeIndex) => {
    updateDayRanges(dayIndex, (ranges) => ranges.filter((_, index) => index !== rangeIndex));
  };

  const updateRange = (dayIndex, rangeIndex, field, value) => {
    updateDayRanges(dayIndex, (ranges) =>
      ranges.map((range, index) =>
        index === rangeIndex
          ? {
            ...range,
            [field]: value,
          }
          : range,
      ),
    );
  };

  const duplicateDay = (sourceDayIndex, targetDayIndex) => {
    const sourceDay = placeForm.openingHours.find((day) => day.day === sourceDayIndex);
    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) =>
        day.day === targetDayIndex
          ? {
            ...day,
            ranges: sourceDay.ranges.map((range) => ({
              ...range,
            })),
          }
          : day,
      ),
    }));
  };
  const isOpen247 = placeForm.openingHours.every(
    (day) => day.ranges.length === 1 && day.ranges[0].isOpen24Hours,
  );
  const setOpen247 = (checked) => {

    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) => ({
        ...day,
        ranges: checked
          ? [
            {
              open: '00:00',
              close: '00:00',
              isOpen24Hours: true,
            },
          ]
          : [],
      })),
    }));
  };

  const savePlace = async (event) => {
    event.preventDefault();
    if (!selectedPlace) return;
    await onSavePlace(selectedPlace.placeId, toPlacePayload(placeForm));
    closePlace();
  };

  const deletePlace = async (confirmationName) => {
    if (!selectedPlace) return;
    await onDeletePlace(selectedPlace.placeId, confirmationName);
    closePlace();
  };

  const togglePlaceFlag = async (event, place, field) => {
    event.stopPropagation();
    event.preventDefault();
    await onSavePlace(place.placeId, {
      [field]: !Boolean(place[field]),
    });
  };

  const columns = [
    {
      key: 'placeId',
      label: 'Place ID',
      render: (place) => place.placeId,
    },
    {
      key: 'nameHe',
      label: 'שם',
      render: (place) => place.name?.he || '-',
    },
    {
      key: 'nameEn',
      label: 'שם סניף',
      render: (place) => place.branchName?.he || '-',
    },
    {
      key: 'city',
      label: 'עיר',
      render: (place) => langValue(place.city),
    },
    {
      key: 'country',
      label: 'מדינה',
      render: (place) => langValue(place.country),
    },
    {
      key: 'address',
      label: 'כתובת',
      render: (place) => langValue(place.address),
    },
    {
      key: 'status',
      label: 'סטטוס',
      render: (place) => (
        <Typography
          sx={String(`pill ${place.status}`)
            .split(/\s+/)
            .map(
              (name) =>
                ({
                  pill: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: '26px',
                    borderRadius: '999px',
                    padding: '0 11px',
                    fontWeight: '800',
                    fontSize: '12px',
                    background: '#eef2f6',
                    color: '#344054',
                  },
                })[name],
            )
            .filter(Boolean)}
        >
          {place.status}
        </Typography>
      ),
    },
    {
      key: 'verified',
      label: 'מאומת',
      render: (place) => (
        <Button
          type="button"
          title={place.isVerified ? 'לחץ לביטול אימות' : 'לחץ להפעלת אימות'}
          aria-label={place.isVerified ? 'בטל אימות מקום' : 'אמת מקום'}
          disabled={loading}
          onClick={(event) => togglePlaceFlag(event, place, 'isVerified')}
          sx={String(`status-dot ${place.isVerified ? 'on' : 'off'}`)
            .split(/\s+/)
            .map(
              (name) =>
                ({
                  'status-dot': {
                    width: '22px',
                    height: '22px',
                    minHeight: '22px',
                    display: 'inline-flex',
                    padding: '0',
                    border: '2px solid white',
                    borderRadius: '999px',
                    boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.12)',
                    verticalAlign: 'middle',
                    '&:hover:not(:disabled)': {
                      transform: 'scale(1.08)',
                    },
                  },
                  on: {
                    background: '#14a904',
                  },
                  off: {
                    background: '#f05353',
                  },
                })[name],
            )
            .filter(Boolean)}
        />
      ),
    },
    {
      key: 'behaviorType',
      label: 'סוג',
      render: (place) => place.behaviorType || '-',
    },
    {
      key: 'placeKind',
      label: 'מבנה מקום',
      render: (place) => {
        const labels = {
          standalone: 'עצמאי',
          container: 'מתחם',
          unit: 'בתוך מתחם',
        };
        return (
          <Typography
            sx={String(`pill place-kind-${place.placeKind || 'standalone'}`)
              .split(/\s+/)
              .map(
                (name) =>
                  ({
                    pill: {
                      display: 'inline-flex',
                      alignItems: 'center',
                      minHeight: '26px',
                      borderRadius: '999px',
                      padding: '0 11px',
                      fontWeight: '800',
                      fontSize: '12px',
                      background: '#eef2f6',
                      color: '#344054',
                    },
                    'place-kind-container': {
                      color: '#155f55',
                      background: '#dff6f1',
                    },
                    'place-kind-unit': {
                      color: '#70420b',
                      background: '#fff0d6',
                    },
                    'place-kind-standalone': {
                      color: '#52605e',
                      background: '#edf1f0',
                    },
                  })[name],
              )
              .filter(Boolean)}
          >
            {labels[place.placeKind] || labels.standalone}
          </Typography>
        );
      },
    },
    {
      key: 'parentPlace',
      label: 'מקום מכיל',
      render: (place) => place.containment?.parentName || place.containment?.parentPlaceId || '-',
    },
    {
      key: 'updatedAt',
      label: 'עודכן',
      render: (place) => formatDate(place.updatedAt),
    },
  ];

  return (
    <Stack
      sx={(theme) => ({
        gap: '22px',
        direction: 'rtl',
        height: '100%',
        minHeight: '0',
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
        overflow: 'hidden',
      })}
    >
      <PageHeader title={<>{pageTitle}</>} meta={`(${places.total})`}>
        <FilterPanel>
          <TextField
            placeholder="חיפוש"
            value={filters.search}
            onChange={(e) =>
              setFilters({
                ...filters,
                search: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            value={filters.status}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: e.target.value,
              })
            }
            select
            fullWidth
          >
            <MenuItem value="">כל הסטטוסים</MenuItem>
            <MenuItem value="active">active</MenuItem>
            <MenuItem value="pending">pending</MenuItem>
            <MenuItem value="rejected">rejected</MenuItem>
          </TextField>

          <TextField
            value={filters.isVerified}
            onChange={(e) =>
              setFilters({
                ...filters,
                isVerified: e.target.value,
              })
            }
            select
            fullWidth
          >
            <MenuItem value="">כל המקומות</MenuItem>
            <MenuItem value="true">מקומות מאומתים</MenuItem>
            <MenuItem value="false">מקומות לא מאומתים</MenuItem>
          </TextField>

          <TextField
            value={filters.footprintStatus}
            onChange={(e) =>
              setFilters({
                ...filters,
                footprintStatus: e.target.value,
              })
            }
            select
            fullWidth
          >
            <MenuItem value="">כל הפוליגונים</MenuItem>
            <MenuItem value="missing">ללא פוליגון</MenuItem>
            <MenuItem value="suggested">ממתינים לאישור</MenuItem>
            <MenuItem value="verified">פוליגון מאומת</MenuItem>
            <MenuItem value="not_found">לא נמצא מבנה</MenuItem>
          </TextField>

          {showPlaceKindFilter && (
            <TextField
              value={filters.placeKind}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  placeKind: e.target.value,
                })
              }
              select
              fullWidth
            >
              <MenuItem value="">כל סוגי המקומות</MenuItem>
              <MenuItem value="standalone">מקומות עצמאיים</MenuItem>
              <MenuItem value="unit">מקומות בתוך מתחם</MenuItem>
            </TextField>
          )}

          <Button onClick={() => loadPlaces(1)}>סינון</Button>
        </FilterPanel>
      </PageHeader>

      <DataTable
        columns={columns}
        items={places.items}
        rowKey={(place) => place.placeId}
        onRowClick={openPlace}
        page={places.page}
        limit={places.limit}
        total={places.total}
        onPageChange={loadPlaces}
        loading={loading}
        selectedRowKey={highlightedPlaceId}
      />

      <PlaceDialog
        open={Boolean(selectedPlace)}
        selectedPlace={selectedPlace}
        placeForm={placeForm}
        setPlaceForm={setPlaceForm}
        updateNested={updateNested}
        addRange={addRange}
        removeRange={removeRange}
        updateRange={updateRange}
        duplicateDay={duplicateDay}
        duplicateTargets={duplicateTargets}
        setDuplicateTargets={setDuplicateTargets}
        setOpen247={setOpen247}
        isOpen247={isOpen247}
        onClose={closePlace}
        onSave={savePlace}
        onLoadNearbyFootprints={onLoadNearbyPlaceFootprints}
        onLoadContainerManagement={onLoadContainerManagement}
        onUpdateContainerFloors={onUpdateContainerFloors}
        onManageContainerFloorUnit={onManageContainerFloorUnit}
        onAssignPlaceToContainer={onAssignPlaceToContainer}
        onRemovePlaceFromContainer={onRemovePlaceFromContainer}
        onSavePlaceFootprint={onSavePlace}
        onDelete={deletePlace}
        behaviorTypes={behaviorTypes}
        categories={categories}
        loading={loading}
      />
    </Stack>
  );
};
export default PlacesPage;
